import React, { createContext, useContext, useState, useEffect, useCallback } from 'react';

const API_URL = process.env.REACT_APP_BACKEND_URL;

const AccessContext = createContext(null);

/**
 * Provides subscription/access level information throughout the app.
 * System admins have full access without needing a subscription.
 */
export const AccessProvider = ({ children }) => {
  const [accessLevel, setAccessLevel] = useState(null);
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState(null);

  const fetchAccessLevel = useCallback(async () => {
    const token = localStorage.getItem('systemklart_token');
    if (!token) {
      setAccessLevel({ level: 'anonymous', has_full_access: false });
      setLoading(false);
      return;
    }

    try {
      const response = await fetch(`${API_URL}/api/v2/ops/access-level`, {
        headers: { 'Authorization': `Bearer ${token}` }
      });
      
      if (response.ok) {
        const data = await response.json();
        setAccessLevel(data);
      } else if (response.status === 401) {
        setAccessLevel({ level: 'anonymous', has_full_access: false });
      } else {
        setError('Failed to fetch access level');
      }
    } catch (err) {
      console.error('Failed to fetch access level:', err);
      setError(err.message);
    } finally {
      setLoading(false);
    }
  }, []);

  useEffect(() => {
    fetchAccessLevel();
  }, [fetchAccessLevel]);

  const refreshAccessLevel = () => {
    setLoading(true);
    fetchAccessLevel();
  };

  // Convenience checks
  const isSystemAdmin = accessLevel?.level === 'system_admin';
  const hasSubscription = accessLevel?.has_full_access === true;
  const needsSubscription = accessLevel?.subscription_required === true;
  const isFree = accessLevel?.level === 'free';
  const isStart = accessLevel?.tier === 'start';
  const isPro = accessLevel?.tier === 'pro' || accessLevel?.tier === 'unlimited';
  const tier = accessLevel?.tier;
  const features = accessLevel?.features || {};
  const usage = accessLevel?.usage || {};
  const limits = accessLevel?.limits || {};

  const canUseFeature = (featureName) => {
    if (isSystemAdmin) return true;
    return features[featureName] === true;
  };

  // Specific feature checks
  const canCreate = isSystemAdmin || accessLevel?.can_create === true;
  const canUseIso = isSystemAdmin || accessLevel?.can_use_iso === true;
  const canUseMiljofyrtarn = isSystemAdmin || accessLevel?.can_use_miljofyrtarn === true;
  const canGenerateIk = isSystemAdmin || accessLevel?.can_generate_ik === true;

  return (
    <AccessContext.Provider value={{
      accessLevel,
      loading,
      error,
      refreshAccessLevel,
      isSystemAdmin,
      hasSubscription,
      needsSubscription,
      isFree,
      isStart,
      isPro,
      tier,
      features,
      usage,
      limits,
      canUseFeature,
      canCreate,
      canUseIso,
      canUseMiljofyrtarn,
      canGenerateIk
    }}>
      {children}
    </AccessContext.Provider>
  );
};

export const useAccess = () => {
  const context = useContext(AccessContext);
  if (!context) {
    throw new Error('useAccess must be used within an AccessProvider');
  }
  return context;
};

export default AccessContext;
